import _ from "lodash";

import {
  Order,
  OrderSet,
  OrderSetStatus,
  OrderType,
  OrderVariant,
  Variant,
} from "@models";
import { StrippedTriggeredRule } from "@models/TriggeredRule";

// fields shared by Order and OrderVariant
export type OrderCosts = Pick<
  Order,
  | "totalActualCost"
  | "totalActualShippingCost"
  | "totalBeaconCost"
  | "totalEstimatedCost"
  | "totalEstimatedShippingCost"
  | "totalEstimatedTax"
  | "billableTotalActualCost"
  | "billableTotalActualShippingCost"
  | "billableTotalBeaconCost"
  | "billableTotalEstimatedCost"
  | "billableTotalEstimatedShippingCost"
  | "billableTotalEstimatedTax"
>;

// Distributors are charged 50% of the cost of the item
// because of co-op pricing
const COOP_PERCENTAGE = 0.5;

export const getCoopCost = (cost: number | string | undefined) => {
  if (!cost) return 0;
  return +cost * COOP_PERCENTAGE;
};

export const getCost = (
  cost: number | string | undefined,
  applyCoopDiscount: boolean
) => {
  if (!cost) return 0;
  if (applyCoopDiscount) {
    return getCoopCost(cost);
  }
  return +cost;
};

// Insures a number is returned
const n = (v: unknown) => (v ? +v : 0);

export const getExtendedCost = (e: OrderCosts) => {
  const shipping = n(
    +e.billableTotalActualShippingCost || e.billableTotalEstimatedShippingCost
  );
  const tax = n(e.billableTotalEstimatedTax);
  const cost = +e.billableTotalActualCost || +e.billableTotalEstimatedCost;

  return cost + shipping + tax + +e.billableTotalBeaconCost;
};

export const getCoopExtendedCost = (e: OrderCosts, orderingType: OrderType) => {
  // We only want estimated costs for coop orders
  const cost = getCoopCost(+e.billableTotalEstimatedCost);
  const tax = getCoopCost(n(e.billableTotalEstimatedTax));

  // Distributors are charged full freight for from-inventory orders
  const shipping = getCost(
    n(e.billableTotalEstimatedShippingCost),
    orderingType !== "from-inventory"
  );

  return cost + shipping + tax + +e.billableTotalBeaconCost;
};

export const getVariantName = (variant: Variant) =>
  variant.selectedVariantOptions.map((option) => option.name).join(" / ");

/** Unique brand names from all order variants, sorted alphabetically. */
export const getOrderBrandNames = (order: Order): string[] =>
  _(order.orderVariants)
    .flatMap((v) => v.variant?.item?.brands?.map((b) => b.name))
    .uniq()
    .sortBy()
    .value();

export const displayOrderSetStatus = (
  status: OrderSetStatus,
  orderSet?: OrderSet
) => {
  if (orderSet?.isWorkComplete && status === "in-progress")
    return "Work Complete";

  const t: Record<OrderSetStatus, string> = {
    inactive: "Not Started",
    "in-progress": "In Progress",
    submitted: "Submitted",
    approved: "Approved",
    canceled: "Canceled",
    "partial-approval-error": "Partial Approval Error",
  };
  return t[status] ?? status;
};

/** Draft catalogs treat missing status as still editable */
export const isActiveDraftOrderSet = (orderSet?: OrderSet | null) =>
  !!orderSet &&
  (!orderSet.status ||
    orderSet.status === "in-progress" ||
    orderSet.status === "inactive");

export const displayToInventoryAllocation = (order: Order) =>
  order.type === "to-inventory"
    ? order.subState
      ? order.subState.name
      : order.territoryId
        ? order.territoryName
        : "National"
    : null;

export const orderTypeLabel = (orderType: OrderType) => {
  const orderTypeMap = {
    "pre-order": "Pre-Order",
    "from-inventory": "Inventory",
    "on-demand": "On-Demand",
    "to-inventory": "Restock",
  };
  return orderTypeMap[orderType] ?? orderType;
};

export const handleComplianceStatus = (rules: StrippedTriggeredRule[]) => {
  if (rules.some((r) => r.status === "denied")) {
    return "Denied";
  }
  if (rules.some((r) => r.status !== "approved")) {
    return "Pending";
  }
  return "Approved";
};

type WithMarketId = { marketId: string | null };
export const isCoopOrder = (o: WithMarketId): boolean => !!o.marketId;

export const isCoopOrderVariant = (ov: WithMarketId): boolean => !!ov.marketId;

export type DistributorOrderVariantCostFields = OrderCosts &
  Pick<
    OrderVariant,
    | "qty"
    | "estimatedCost"
    | "marketId"
    | "orderOrderingType"
    | "distributorApprovedCost"
    | "distributorApprovedShippingCost"
    | "distributorApprovedTax"
    | "distributorUnitProductCost"
    | "distributorTotalProductCost"
  >;

export type DistributorOrderCostFields = OrderCosts &
  Pick<Order, "marketId" | "orderingType"> & {
    orderVariants: DistributorOrderVariantCostFields[];
  };

/** What the distributor is billed, as shown in distributor Order History. */
export type DistributorBilledCosts = {
  productCost: number;
  shippingCost: number;
  tax: number;
  allInCost: number;
};

// A zero snapshot is a real approval (SAP still bills the one-cent minimum), so
// only null/undefined means the order variant hasn't been approved.
const hasDistributorApprovedSnapshot = (
  ov: Pick<OrderVariant, "distributorApprovedCost">
): boolean => !_.isNil(ov.distributorApprovedCost);

/**
 * Unit price billed to the distributor. After approval this is the frozen,
 * cent-rounded snapshot; before approval it is the co-op estimate.
 */
export const getDistributorUnitProductCost = (
  ov: DistributorOrderVariantCostFields
): number =>
  hasDistributorApprovedSnapshot(ov)
    ? n(ov.distributorUnitProductCost)
    : getCost(ov.estimatedCost, isCoopOrderVariant(ov));

/**
 * After approval, costs come from the frozen snapshot SAP invoices from, so
 * later repricing of estimated costs does not change them. Before approval
 * (or for orders never snapshotted) they are the co-op estimate.
 */
export const getDistributorOrderVariantCosts = (
  ov: DistributorOrderVariantCostFields
): DistributorBilledCosts => {
  if (hasDistributorApprovedSnapshot(ov)) {
    const productCost = n(ov.distributorTotalProductCost);
    const shippingCost = n(ov.distributorApprovedShippingCost);
    const tax = n(ov.distributorApprovedTax);
    return {
      productCost,
      shippingCost,
      tax,
      allInCost: productCost + shippingCost + tax,
    };
  }

  const isCoop = isCoopOrderVariant(ov);
  return {
    productCost: getCost(ov.billableTotalEstimatedCost, isCoop),
    shippingCost: getCost(
      ov.billableTotalEstimatedShippingCost,
      isCoop && ov.orderOrderingType !== "from-inventory"
    ),
    tax: getCost(ov.billableTotalEstimatedTax, isCoop),
    allInCost: isCoop
      ? getCoopExtendedCost(ov, ov.orderOrderingType)
      : getExtendedCost(ov),
  };
};

/**
 * Order-level totals are the sum of the order variants' snapshots once the
 * order is approved, since the order itself has no snapshot fields.
 */
export const getDistributorOrderCosts = (
  order: DistributorOrderCostFields
): DistributorBilledCosts => {
  if (order.orderVariants.some(hasDistributorApprovedSnapshot)) {
    const ovCosts = order.orderVariants.map(getDistributorOrderVariantCosts);
    return {
      productCost: _.sumBy(ovCosts, "productCost"),
      shippingCost: _.sumBy(ovCosts, "shippingCost"),
      tax: _.sumBy(ovCosts, "tax"),
      allInCost: _.sumBy(ovCosts, "allInCost"),
    };
  }

  const isCoop = isCoopOrder(order);
  return {
    productCost: getCost(order.billableTotalEstimatedCost, isCoop),
    shippingCost: getCost(
      order.billableTotalEstimatedShippingCost,
      isCoop && order.orderingType !== "from-inventory"
    ),
    tax: getCost(order.billableTotalEstimatedTax, isCoop),
    allInCost: isCoop
      ? getCoopExtendedCost(order, order.orderingType)
      : getExtendedCost(order),
  };
};

/**
 * we want to use the order's original ordering type for this calculation
 * so it doesn't change for the client after the PO is created.
 */
type WithEstimatedShippingCost = Pick<
  Order,
  "totalEstimatedShippingCost" | "marketId" | "orderingType"
>;

const getEstimatedShippingCost = (o: WithEstimatedShippingCost): number => {
  const coopOrder = isCoopOrder(o);
  const isInventoryOrder = o.orderingType === "from-inventory";
  // We double the freight for coop orders on inventory orders
  // as this represents freight costs to and from the warehouse
  return coopOrder && isInventoryOrder
    ? +o.totalEstimatedShippingCost * 2
    : +o.totalEstimatedShippingCost;
};

export const getOrderEstimatedShippingCost = (o: Order): number => {
  return getEstimatedShippingCost({
    totalEstimatedShippingCost: o.totalEstimatedShippingCost,
    marketId: o.marketId,
    orderingType: o.orderingType,
  });
};

export const getOrderVariantEstimatedShippingCost = (
  ov: OrderVariant
): number => {
  return getEstimatedShippingCost({
    totalEstimatedShippingCost: ov.totalEstimatedShippingCost,
    marketId: ov.marketId,
    orderingType: ov.orderOrderingType,
  });
};
